import React, { useState, useEffect, useRef } from "react";
import {
  FiTrendingDown,
  FiTrendingUp,
  FiFilter,
  FiChevronDown,
  FiUsers,
  FiGlobe,
  FiHome,
  FiBook,
  FiHeart,
  FiUser,
  FiDollarSign,
  FiCheckCircle,
  FiActivity,
  FiAlertCircle
} from "react-icons/fi";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from "recharts";
import API_BASE_URL from "../../config/api";
import { getZoneColor } from "../../utils/zoneUtils";

const CustomSelect = ({ label, value, options, onChange, disabled = false }: any) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedOption = options.find((o:any) => o.value === value) || options[0];

  return (
    <div className={`relative group ${disabled ? 'opacity-50 pointer-events-none' : ''}`} ref={dropdownRef}>
      <label className="absolute -top-2.5 left-3 px-1 bg-white text-[10px] font-bold uppercase tracking-wider z-10 rounded text-[#1ecab8]">
        {label}
      </label>
      <div 
        className="relative flex items-center justify-between min-w-[160px] pl-4 pr-3 py-2.5 bg-white border border-gray-300 hover:border-[#1ecab8]/30 rounded-xl text-sm font-medium text-gray-700 outline-none focus:ring-[#1ecab8]/10 transition-all cursor-pointer shadow-sm"
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <span className="truncate pr-2">{selectedOption?.label}</span>
        <FiChevronDown size={18} className={`text-gray-400 shrink-0 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
      </div>
      {isOpen && !disabled && (
        <div className="absolute top-full left-0 mt-2 w-full bg-white border border-gray-100 rounded-xl shadow-[0_10px_40px_rgb(0,0,0,0.12)] py-1.5 z-50">
          {options.map((opt:any) => (
            <div
              key={opt.value}
              onClick={() => { onChange(opt.value); setIsOpen(false); }}
              className={`px-4 py-2.5 text-sm font-medium cursor-pointer transition-colors ${value === opt.value ? 'bg-[#1ecab8]/10 text-[#1ecab8]' : 'text-gray-600 hover:bg-[#1ecab8]/5'}`}
            >
              {opt.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const WellbeingAnalysis: React.FC = () => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const [filters, setFilters] = useState({
    period: "current_month",
    board: "all",
    grade: "all",
    className: "all",
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams({
        period: filters.period,
        ...(filters.board !== "all" && { board: filters.board }),
        ...(filters.grade !== "all" && { grade: filters.grade }),
        ...(filters.className !== "all" && { className: filters.className }),
      });
      const response = await fetch(`${API_BASE_URL}/schools/analytics/wellbeing?${queryParams}`);
      const result = await response.json();
      setData(result);
    } catch (error) {
      console.error("Failed to fetch wellbeing analysis:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [filters]);

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => {
      const newFilters = { ...prev, [key]: value };
      if (key === "grade" && value === "all") newFilters.className = "all";
      return newFilters;
    });
  };

  const getScoreColor = (score: number) => {
    if (score >= 6.5) return getZoneColor("Green Zone").color;
    if (score >= 5.5) return getZoneColor("Blue Zone").color;
    if (score >= 4.5) return getZoneColor("Yellow Zone").color;
    if (score >= 3.5) return getZoneColor("Orange Zone").color;
    if (score >= 2.5) return getZoneColor("Brown Zone").color;
    if (score >= 1.5) return getZoneColor("Red Zone").color;
    return getZoneColor("Dark Red").color;
  };

  const getCategoryIcon = (categoryName: string) => {
    const name = categoryName.toLowerCase();
    if (name.includes('friend')) return <div className="p-2 bg-indigo-50 text-indigo-500 rounded-full"><FiUsers size={16}/></div>;
    if (name.includes('society') || name.includes('environment')) return <div className="p-2 bg-emerald-50 text-emerald-500 rounded-full"><FiGlobe size={16}/></div>;
    if (name.includes('family')) return <div className="p-2 bg-amber-50 text-amber-500 rounded-full"><FiHome size={16}/></div>;
    if (name.includes('education') || name.includes('academic')) return <div className="p-2 bg-blue-50 text-blue-500 rounded-full"><FiBook size={16}/></div>;
    if (name.includes('health') || name.includes('fitness')) return <div className="p-2 bg-teal-50 text-teal-500 rounded-full"><FiHeart size={16}/></div>;
    if (name.includes('teacher')) return <div className="p-2 bg-orange-50 text-orange-500 rounded-full"><FiUser size={16}/></div>;
    if (name.includes('self')) return <div className="p-2 bg-rose-50 text-rose-500 rounded-full"><FiUser size={16}/></div>;
    if (name.includes('money') || name.includes('finance')) return <div className="p-2 bg-indigo-50 text-indigo-500 rounded-full"><FiDollarSign size={16}/></div>;
    return <div className="p-2 bg-gray-50 text-gray-500 rounded-full"><FiActivity size={16}/></div>;
  };

  const CustomActiveDot = (props: any) => {
    const { cx, cy, value } = props;
    return (
      <g>
        <circle cx={cx} cy={cy} r={4} fill={getZoneColor("Green Zone").color} />
        <text x={cx} y={cy - 10} textAnchor="middle" fill="#334155" fontSize="12" fontWeight="bold">{value}</text>
      </g>
    );
  };

  if (loading || !data) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1ecab8]"></div></div>;
  }

  const totalDistressStudents = data.categoryHotspots?.reduce((sum:number, h:any) => sum + h.distressCount, 0) || 0;
  const totalStudentsOverall = data.coverageData?.totalStudentsInCohort || 1; // avoid /0
  const distressPctOverall = ((totalDistressStudents / totalStudentsOverall) * 100).toFixed(1);

  return (
    <div className="space-y-6 animate-fadeIn pb-12 bg-slate-50 min-h-screen p-6">
      
      {/* TOP ROW: Filters & Coverage */}
      <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-200 flex flex-col xl:flex-row items-center justify-between gap-6">
        <div className="flex flex-wrap items-center gap-4 w-full xl:w-auto">
          <CustomSelect label="Period" value={filters.period} options={[{ value: "current_month", label: "Current Month" }, { value: "last_month", label: "Last Month" }, { value: "last_quarter", label: "Last Quarter" }, { value: "year", label: "Year to Date" }]} onChange={(val: string) => handleFilterChange("period", val)} />
          <CustomSelect label="Board" value={filters.board} options={[{ value: "all", label: "All Boards" }, { value: "CBSE", label: "CBSE" }, { value: "ICSE", label: "ICSE" }]} onChange={(val: string) => handleFilterChange("board", val)} />
          <CustomSelect label="Grade" value={filters.grade} options={[{ value: "all", label: "All Grades" }, { value: "Grade 8", label: "Grade 8" }, { value: "Grade 9", label: "Grade 9" }, { value: "Grade 10", label: "Grade 10" }, { value: "Grade 11", label: "Grade 11" }, { value: "Grade 12", label: "Grade 12" }]} onChange={(val: string) => handleFilterChange("grade", val)} />
          <CustomSelect label="Class" value={filters.className} options={[{ value: "all", label: "All Classes" }, { value: "A", label: "Section A" }, { value: "B", label: "Section B" }, { value: "C", label: "Section C" }]} onChange={(val: string) => handleFilterChange("className", val)} disabled={filters.grade === "all"} />
        </div>
        
        {data.coverageData && (
          <div className="flex items-center gap-4 w-full xl:w-auto xl:justify-end pr-4">
            <div className="p-3 bg-emerald-50 text-emerald-500 rounded-full">
               <FiUsers size={20} />
            </div>
            <div>
               <p className="text-xs font-bold text-slate-400 uppercase tracking-wide">Coverage</p>
               <div className="flex items-end gap-2">
                 <span className="text-xl font-black text-slate-800">{data.coverageData.coveragePct}%</span>
               </div>
               <p className="text-[11px] text-slate-500">{data.coverageData.uniqueStudentsLogged} / {data.coverageData.totalStudentsInCohort} students</p>
            </div>
          </div>
        )}
      </div>

      {/* BOTTOM ROW */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        
        {/* Historical Trend */}
        <div className="xl:col-span-2 bg-white rounded-xl p-6 shadow-sm border border-slate-200">
            <div className="flex items-center gap-2 mb-6">
              <FiTrendingUp className="text-emerald-500" size={20} />
              <div>
                <h3 className="text-base font-bold text-slate-900">Historical Trend (Overall Wellbeing)</h3>
                <p className="text-xs text-slate-500">12-month overall wellbeing score for the selected cohort</p>
              </div>
            </div>
            <div className="h-[220px] w-full mt-4">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data.trend} margin={{ top: 20, right: 20, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                  <XAxis dataKey="month" stroke="#94a3b8" fontSize={11} tickLine={false} axisLine={false} dy={10} minTickGap={15} />
                  <YAxis domain={[1, 7]} ticks={[1,3,5,7]} stroke="#94a3b8" fontSize={12} tickLine={false} axisLine={false} />
                  <Tooltip contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }} />
                  <Line type="monotone" dataKey="score" stroke="#10b981" strokeWidth={2} dot={<CustomActiveDot />} />
                </LineChart>
              </ResponsiveContainer>
            </div>
        </div>

        {/* Students in Distress */}
        <div className="xl:col-span-1 bg-white rounded-xl p-6 shadow-sm border border-slate-200">
           <div className="flex items-center gap-2 mb-2">
              <FiAlertCircle className="text-rose-500" size={20} />
              <h3 className="text-base font-bold text-slate-900">Students in Distress by Category</h3>
           </div>
           <p className="text-xs text-slate-500 mb-6 ml-7">No. of students in Red/Dark Red zone</p>

           <div className="grid grid-cols-12 gap-2 pb-2 border-b border-slate-100 text-xs font-semibold text-slate-500 mb-3">
              <div className="col-span-6">Category</div>
              <div className="col-span-4 text-right">Students</div>
              <div className="col-span-2 text-right">% of Cohort</div>
           </div>

           <div className="space-y-3">
             {data.categoryHotspots?.map((item:any) => (
                <div key={item.category} className="grid grid-cols-12 gap-2 items-center">
                   <div className="col-span-5 truncate text-xs font-semibold text-slate-700">{item.category}</div>
                   <div className="col-span-3">
                      <div className="w-full bg-transparent h-2.5">
                         <div className="h-full rounded-r-full bg-rose-500" style={{ width: `${Math.max(item.distressPct, 5)}%` }}></div>
                      </div>
                   </div>
                   <div className="col-span-2 text-right text-xs font-semibold text-slate-700">{item.distressCount}</div>
                   <div className="col-span-2 text-right text-xs text-slate-500">{item.distressPct.toFixed(1)}%</div>
                </div>
             ))}
           </div>

           <div className="mt-6 pt-4 border-t border-slate-100 flex items-center justify-between">
              <span className="text-sm font-bold text-slate-800">Total Students in Distress</span>
              <span className="text-sm font-bold text-rose-500">{totalDistressStudents} ({distressPctOverall}%)</span>
           </div>
        </div>

      </div>


      {/* MIDDLE ROW */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        
        {/* Category Wellbeing Score Table */}
        <div className="xl:col-span-2 bg-white rounded-xl p-6 shadow-sm border border-slate-200">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-bold text-slate-900">Category Wellbeing Score</h3>
              <p className="text-xs text-slate-500">Average wellbeing score (1-7) by life category</p>
            </div>
            <div className="flex items-center gap-2 text-xs text-slate-500 font-medium">
              <FiTrendingUp /> vs. Last Month
            </div>
          </div>

          <div className="w-full text-left">
            <div className="grid grid-cols-12 gap-4 pb-3 border-b border-slate-100 text-xs font-semibold text-slate-500">
              <div className="col-span-4 pl-2">Category</div>
              <div className="col-span-4 text-center">Score (1-7)</div>
              <div className="col-span-2 text-center">vs. Last Month</div>
              <div className="col-span-2 text-center">Trend</div>
            </div>
            
            <div className="space-y-1 mt-2">
              {data.categoryWellbeingScores?.map((cat: any) => (
                <div key={cat.category} className="grid grid-cols-12 gap-4 items-center py-2.5 hover:bg-slate-50 rounded-lg transition-colors">
                  <div className="col-span-4 flex items-center gap-3">
                    {getCategoryIcon(cat.category)}
                    <span className="text-sm font-semibold text-slate-700 truncate">{cat.category}</span>
                  </div>
                  
                  <div className="col-span-4 flex items-center justify-center gap-4">
                    <div className="w-full max-w-[120px] bg-slate-100 rounded-full h-2.5 overflow-hidden">
                      <div className="h-full rounded-full transition-all duration-700" style={{ width: `${(cat.score / 7) * 100}%`, backgroundColor: getScoreColor(cat.score) }} />
                    </div>
                    <span className="text-sm font-bold text-slate-800 w-8">{cat.score}</span>
                  </div>
                  
                  <div className="col-span-2 flex items-center justify-center">
                    <div className={`flex items-center gap-1 text-sm font-bold ${cat.trend > 0 ? 'text-emerald-500' : cat.trend < 0 ? 'text-rose-500' : 'text-slate-400'}`}>
                       {cat.trend > 0 ? '▲' : cat.trend < 0 ? '▼' : '-'} {Math.abs(cat.trend)}
                    </div>
                  </div>

                  <div className="col-span-2 h-8">
                     <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={cat.history}>
                          <Line type="monotone" dataKey="score" stroke={cat.trend >= 0 ? "#10b981" : "#ef4444"} strokeWidth={1.5} dot={false} />
                        </LineChart>
                     </ResponsiveContainer>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-6 mt-8 pt-6 border-t border-slate-100 text-[11px] font-semibold text-slate-500">
             <div className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{backgroundColor: getZoneColor("Dark Red").color}}></span> 1 (Dark Red)</div>
             <div className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{backgroundColor: getZoneColor("Red Zone").color}}></span> 2 (Red)</div>
             <div className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{backgroundColor: getZoneColor("Brown Zone").color}}></span> 3 (Brown)</div>
             <div className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{backgroundColor: getZoneColor("Orange Zone").color}}></span> 4 (Orange)</div>
             <div className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{backgroundColor: getZoneColor("Yellow Zone").color}}></span> 5 (Yellow)</div>
             <div className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{backgroundColor: getZoneColor("Blue Zone").color}}></span> 6 (Blue)</div>
             <div className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{backgroundColor: getZoneColor("Green Zone").color}}></span> 7 (Green)</div>
          </div>
        </div>

        {/* What's Working Well */}
        <div className="xl:col-span-1 bg-white rounded-xl p-6 shadow-sm border border-slate-200">
           <div className="flex items-center gap-2 mb-6">
              <div className="p-1 rounded-full text-emerald-500 border-2 border-emerald-500"><FiCheckCircle size={16} /></div>
              <h3 className="text-lg font-bold text-slate-900">What's Working Well</h3>
           </div>
           
           <div className="space-y-6">
              {data.positiveInsights?.map((insight:any, idx:number) => (
                <div key={idx} className="flex gap-4">
                   <div className="shrink-0 mt-1">
                      {insight.icon === 'highest' ? <div className="p-2 bg-indigo-50 text-indigo-500 rounded-lg"><FiUsers size={16}/></div> :
                       insight.icon === 'improvement' ? <div className="p-2 bg-emerald-50 text-emerald-500 rounded-lg"><FiTrendingUp size={16}/></div> :
                       <div className="p-2 bg-blue-50 text-blue-500 rounded-lg"><FiBook size={16}/></div>}
                   </div>
                   <div>
                      <p className="text-sm font-bold text-slate-800 leading-tight mb-1">{insight.title}</p>
                      <p className="text-xs text-slate-500">{insight.desc}</p>
                   </div>
                </div>
              ))}
              {(!data.positiveInsights || data.positiveInsights.length === 0) && (
                  <p className="text-sm text-slate-500 italic">No significant positive trends detected in this period.</p>
              )}
           </div>
        </div>

      </div>

          </div>
  );
};

export default WellbeingAnalysis;
